import { useEffect, useState } from "react";
import type { IconName } from "../../../components/Icon/Icon.tsx";
import { TagInput } from "../../../components/TagInput/TagInput.tsx";
import { Textarea } from "../../../components/Textarea/Textarea.tsx";
import { TextField } from "../../../components/TextField/TextField.tsx";
import { useAppMutation } from "../../../http/useAppMutation.ts";
import { useAppQuery } from "../../../http/useAppQuery.ts";
import { useDebugMode } from "../../components/DebugMode/hooks/useDebugMode.ts";
import {
  CHARACTER_MODEL_TYPE,
  OBJECT_MODEL_TYPE,
  STYLE_MODEL_TYPE,
} from "../../constants.ts";
import { useUser } from "../../hooks/useUser.ts";
import type {
  CharacterDetails,
  CharacterModel,
  Graphic3DStyleModel,
  IconStyleModel,
  IllustrationColorDetails,
  IllustrationStyleModel,
  ModelWithContents,
  ObjectDetails,
  ObjectModel,
  PhotographyColorDetails,
  PhotographyStyleDetails,
  PhotographyStyleModel,
  StyleModel,
} from "../../types.ts";
import { ModelTypeDisplay } from "./constants.ts";

export const ModelDetails = ({ modelUuid }: { modelUuid: string }) => {
  const { mutate: updateModel } = useAppMutation({
    path: "references/update",
    invalidate: [`references/${modelUuid}`],
  }).mutation;

  const { data: model } = useAppQuery<ModelWithContents>({
    queryKey: `references/${modelUuid}`,
  });
  const { user } = useUser();

  // XXX: User can update model only if the model is its own or if the user has the permission to update curated models
  const isModelUpdateDisabled =
    model?.is_curated &&
    !user?.permissions.includes("references:update-curated-references");

  const [additionalElements, setAdditionalElements] = useState<string>("");

  useEffect(() => {
    setAdditionalElements(model?.additional_elements ?? "");
  }, [model]);

  const isDebugModeOn = useDebugMode();

  return (
    <div className="w-full grid grid-cols-[3fr_7fr] gap-200">
      {(() => {
        switch (model?.type) {
          case STYLE_MODEL_TYPE:
            return (
              <StyleModelDetails
                model={model}
                isModelUpdateDisabled={isModelUpdateDisabled ?? false}
              />
            );
          case CHARACTER_MODEL_TYPE:
            // FIXME: We currently display the character model details only for debug users until we validate the different categories and labels
            return (
              isDebugModeOn && (
                <CharacterModelDetails
                  model={model}
                  isModelUpdateDisabled={isModelUpdateDisabled ?? false}
                />
              )
            );
          case OBJECT_MODEL_TYPE:
            return (
              <ObjectModelDetails
                model={model}
                isModelUpdateDisabled={isModelUpdateDisabled ?? false}
              />
            );
          case undefined:
            return null;
        }
      })()}
      <div className="py-100 label-md-semibold text-disabled">
        Additional elements
      </div>
      <Textarea
        value={additionalElements}
        onChange={setAdditionalElements}
        size="sm"
        placeholder="Additional elements"
        onBlur={() =>
          updateModel({
            uuid: modelUuid,
            additional_elements: additionalElements,
          })
        }
        disabled={isModelUpdateDisabled}
      />
    </div>
  );
};

const StyleModelDetails = ({
  model,
  isModelUpdateDisabled,
}: {
  model: StyleModel;
  isModelUpdateDisabled: boolean;
}) => (
  <>
    {model.images_kind && (
      <>
        <div className="py-100 label-md-semibold text-disabled">Type</div>
        <TextField
          value={ModelTypeDisplay[model.images_kind]}
          onChange={() => null}
          size="sm"
          placeholder="Style type"
          disabled
        />
      </>
    )}
    {(() => {
      switch (model.images_kind) {
        case "PHOTOGRAPHY":
          return (
            <PhotographyModelStyleDetails
              model={model}
              isModelUpdateDisabled={isModelUpdateDisabled}
            />
          );
        case "ILLUSTRATION_2D":
        case "GRAPHIC_3D":
        case "ICON":
          return (
            <NonPhotographicModelStyleDetails
              model={model}
              isModelUpdateDisabled={isModelUpdateDisabled}
            />
          );
        case "OTHER":
        case null:
          return null;
      }
    })()}
  </>
);

const PhotographyModelStyleDetails = ({
  model,
  isModelUpdateDisabled,
}: {
  model: PhotographyStyleModel;
  isModelUpdateDisabled: boolean;
}) => {
  const { mutate: updateModel } = useAppMutation({
    path: "references/update",
    invalidate: [`references/${model.uuid}`],
  }).mutation;

  const [colorDetails, setColorDetails] =
    useState<PhotographyColorDetails | null>(null);
  const [styleDetails, setStyleDetails] =
    useState<PhotographyStyleDetails | null>(null);

  useEffect(() => {
    setColorDetails(model.color_details);
    setStyleDetails(model.details);
  }, [model]);

  const updateStyleDetail = (field: string, value: string[]) => {
    if (!styleDetails) return;

    const newDetails = { ...styleDetails, [field]: value };
    setStyleDetails(newDetails);
    updateModel({
      uuid: model.uuid,
      [field]: value,
    });
  };

  return (
    <>
      <TagField
        label="Colors"
        tags={colorDetails?.palette ?? []}
        onChange={(tags) => {
          setColorDetails({ palette: tags });
          updateModel({
            uuid: model.uuid,
            color_palette: tags,
          });
        }}
        iconName="Palette"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Exposure"
        tags={styleDetails?.exposure ?? []}
        onChange={(tags) => updateStyleDetail("exposure", tags)}
        iconName="LampCeiling"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Composition"
        tags={styleDetails?.composition ?? []}
        onChange={(tags) => updateStyleDetail("composition", tags)}
        iconName="Frame"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Focus"
        tags={styleDetails?.focus ?? []}
        onChange={(tags) => updateStyleDetail("focus", tags)}
        iconName="Focus"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Quality"
        tags={styleDetails?.image_quality ?? []}
        onChange={(tags) => updateStyleDetail("image_quality", tags)}
        iconName="ImageOff"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Camera type"
        tags={styleDetails?.camera_type ?? []}
        onChange={(tags) => updateStyleDetail("camera_type", tags)}
        iconName="Video"
        isDisabled={isModelUpdateDisabled}
      />
    </>
  );
};

const NonPhotographicModelStyleDetails = ({
  model,
  isModelUpdateDisabled,
}: {
  model: IllustrationStyleModel | IconStyleModel | Graphic3DStyleModel;
  isModelUpdateDisabled: boolean;
}) => {
  const { mutate: updateModel } = useAppMutation({
    path: "references/update",
    invalidate: [`references/${model.uuid}`],
  }).mutation;

  const [colorDetails, setColorDetails] =
    useState<IllustrationColorDetails | null>(null);
  const [styleDetails, setStyleDetails] = useState({
    composition: [] as string[],
    level_of_detail: [] as string[],
    shape: [] as string[],
    stroke: [] as string[],
  });

  const hasStroke =
    model.images_kind === "ILLUSTRATION_2D" || model.images_kind === "ICON";

  useEffect(() => {
    setColorDetails(model.color_details);
    setStyleDetails({
      composition: model.details?.composition ?? [],
      level_of_detail: model.details?.level_of_detail ?? [],
      shape: model.details?.shape ?? [],
      stroke: hasStroke && model.details?.stroke ? model.details.stroke : [],
    });
  }, [model, hasStroke]);

  const updateStyleDetails = (field: string, value: string[]) => {
    setStyleDetails((prev) => ({ ...prev, [field]: value }));
    updateModel({
      uuid: model.uuid,
      [field]: value,
    });
  };

  return (
    <>
      <TagField
        label="Colors"
        tags={colorDetails?.palette ?? []}
        onChange={(tags) => {
          setColorDetails({
            palette: tags,
            texture: colorDetails?.texture ?? [],
          });
          updateModel({
            uuid: model.uuid,
            color_palette: tags,
          });
        }}
        iconName="Palette"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Texture"
        tags={colorDetails?.texture ?? []}
        onChange={(tags) => {
          setColorDetails({
            palette: colorDetails?.palette ?? [],
            texture: tags,
          });
          updateModel({
            uuid: model.uuid,
            color_texture: tags,
          });
        }}
        iconName="LampDesk"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Composition"
        tags={styleDetails.composition}
        onChange={(tags) => updateStyleDetails("composition", tags)}
        iconName="Frame"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Level of detail"
        tags={styleDetails.level_of_detail}
        onChange={(tags) => updateStyleDetails("level_of_detail", tags)}
        iconName="Gauge"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Shape"
        tags={styleDetails.shape}
        onChange={(tags) => updateStyleDetails("shape", tags)}
        iconName="Cone"
        isDisabled={isModelUpdateDisabled}
      />

      {hasStroke && (
        <TagField
          label="Stroke"
          tags={styleDetails.stroke}
          onChange={(tags) => updateStyleDetails("stroke", tags)}
          iconName="PencilLine"
          isDisabled={isModelUpdateDisabled}
        />
      )}
    </>
  );
};

const ObjectModelDetails = ({
  model,
  isModelUpdateDisabled,
}: {
  model: ObjectModel;
  isModelUpdateDisabled: boolean;
}) => {
  const { mutate: updateModel } = useAppMutation({
    path: "references/update",
    invalidate: [`references/${model.uuid}`],
  }).mutation;

  const [objectDetails, setObjectDetails] = useState<ObjectDetails | null>(
    null,
  );

  useEffect(() => {
    setObjectDetails(model.details);
  }, [model]);

  const updateObjectDetails = (field: string, tags: string[]) => {
    const newDetails = {
      materials: field === "materials" ? tags : objectDetails?.materials ?? [],
      labels: field === "labels" ? tags : objectDetails?.labels ?? [],
    };
    setObjectDetails(newDetails);
    updateModel({
      uuid: model.uuid,
      [field]: tags,
    });
  };

  return (
    <>
      <TagField
        label="Materials"
        tags={objectDetails?.materials ?? []}
        onChange={(tags) => updateObjectDetails("materials", tags)}
        iconName="BrickWall"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Labels"
        tags={objectDetails?.labels ?? []}
        onChange={(tags) => updateObjectDetails("labels", tags)}
        iconName="Type"
        isDisabled={isModelUpdateDisabled}
      />
    </>
  );
};

const CharacterModelDetails = ({
  model,
  isModelUpdateDisabled,
}: {
  model: CharacterModel;
  isModelUpdateDisabled: boolean;
}) => {
  const { mutate: updateModel } = useAppMutation({
    path: "references/update",
    invalidate: [`references/${model.uuid}`],
  }).mutation;

  const [characterDetails, setCharacterDetails] =
    useState<CharacterDetails | null>(null);

  useEffect(() => {
    setCharacterDetails(model.details);
  }, [model]);

  const updateCharacterDetails = (field: string, tags: string[]) => {
    if (!characterDetails) return;
    const newDetails = { ...characterDetails, [field]: tags };
    setCharacterDetails(newDetails);
    updateModel({
      uuid: model.uuid,
      [field]: tags,
    });
  };

  return (
    <>
      <TagField
        label="Gender"
        tags={characterDetails?.gender ?? []}
        onChange={(tags) => updateCharacterDetails("gender", tags)}
        iconName="User"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Age"
        tags={characterDetails?.age ?? []}
        onChange={(tags) => updateCharacterDetails("age", tags)}
        iconName="Calendar"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Hair"
        tags={characterDetails?.hair ?? []}
        onChange={(tags) => updateCharacterDetails("hair", tags)}
        iconName="Scissors"
        isDisabled={isModelUpdateDisabled}
      />

      <TagField
        label="Clothing"
        tags={characterDetails?.clothing ?? []}
        onChange={(tags) => updateCharacterDetails("clothing", tags)}
        iconName="Shirt"
        isDisabled={isModelUpdateDisabled}
      />
    </>
  );
};

const TagField = ({
  label,
  tags,
  onChange,
  iconName,
  isDisabled,
}: {
  label: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  iconName: IconName;
  isDisabled: boolean;
}) => (
  <>
    <div className="py-100 label-md-semibold text-disabled">{label}</div>
    <TagInput
      tagValues={tags}
      onChange={onChange}
      size="sm"
      leftIconName={iconName}
      isDisabled={isDisabled}
    />
  </>
);
